import { useCallback, useEffect, useRef, useState } from "react";
import { getDayRecordByDate, getTodayDateString, markWebAttendance } from "./facialRecognition";
import { apiError } from "../utils/formErrors";

/**
 * Today's clock-in state, kept in step with the server — the one place it is
 * read on the web.
 *
 * The employee and admin dashboards each had their own copy of this, and only
 * one was ever fixed to stay current: an admin's card read the record once, on
 * load, and kept showing "Clock in" after a punch from the phone. Pressing it
 * was then refused as a repeat, and the card stayed wrong. Shared, it re-reads
 * when the tab comes back into view, once a minute while open, after every
 * punch, and after a refused one — so a stale card corrects itself instead of
 * repeating the mistake.
 *
 * The server decides what is allowed; this only mirrors it.
 */
const useTodayAttendance = () => {
  const [state, setState] = useState({ inTime: null, outTime: null, isCheckedIn: false, loaded: false });
  const [punching, setPunching] = useState(false);
  const live = useRef(true);

  const refresh = useCallback(async () => {
    try {
      const today = await getTodayDateString();
      const dateString = today?.data?.dateString;
      const record = dateString ? await getDayRecordByDate(dateString) : null;
      if (!live.current) return;
      if (record?.data) {
        setState({
          inTime: record.data.createdAt,
          outTime: record.data.departureTime || null,
          isCheckedIn: !record.data.departureTime,
          loaded: true,
        });
      } else if (today?.success !== false) {
        // No punch today — a new day, or nothing yet. Cleared rather than left
        // as it was, or a tab open overnight still says "clocked in".
        setState({ inTime: null, outTime: null, isCheckedIn: false, loaded: true });
      }
    } catch {
      // non-blocking — the card keeps what it last knew
    }
  }, []);

  useEffect(() => {
    live.current = true;
    refresh();
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVisible);
    const every = setInterval(() => { if (document.visibilityState === "visible") refresh(); }, 60000);
    return () => {
      live.current = false;
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(every);
    };
  }, [refresh]);

  /** Clock in or out — whichever the server last said is next. Throws on refusal. */
  const punch = useCallback(async () => {
    const type = state.isCheckedIn ? "OUT" : "IN";
    setPunching(true);
    try {
      const res = await markWebAttendance({ type });
      if (res?.success === false) throw apiError(res);
      await refresh();
      return type;
    } catch (err) {
      // most often: the phone punched first and this card was behind
      await refresh();
      throw err;
    } finally {
      if (live.current) setPunching(false);
    }
  }, [state.isCheckedIn, refresh]);

  return { ...state, punching, punch, refresh };
};

export default useTodayAttendance;
